import {Injectable} from '@angular/core'
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router'
import {Observable, of} from 'rxjs'
import {ResolverUtilService} from '../../../core/resolver/resolver-util.service'
import {Offering} from '../../../core/content/types/offering.types'
import {catchError, map, mergeMap} from 'rxjs/operators'
import {OfferingPage} from '../../../core/content/types/offering-page.types'
import {
  OfferingPage_GetOfferingIntentionsByIdGql,
  ProDirectoryPage_GetIndustriesGql,
} from '../../../../__generated__/types'
import {OfferingPageContentService} from '../../../core/content/services/offering-page-content.service'
import {PageMeta} from '../../../core/content/types/seo.types'
import {pickProperty} from '../../../core/graphql/graphql-utils'

type IndustryOffering = {__type?: 'Offering'; id: string; label: string; slug?: string}
export type OfferingPageOffering = Offering & {intentions: {slug: string; label: string}[]}

export type OfferingPageResolved = OfferingPageOffering &
  OfferingPage & {
    cover: {
      titleProps: {offering: string}
      subtitleProps: {offering: string}
    }
    relatedOfferings: IndustryOffering[]
    seo?: PageMeta
  }

@Injectable()
export class OfferingPageResolver {
  constructor(
    private readonly contentService: OfferingPageContentService,
    private readonly util: ResolverUtilService,
    private readonly getOfferingIntentionsGql: OfferingPage_GetOfferingIntentionsByIdGql,
    private readonly getIndustriesGql: ProDirectoryPage_GetIndustriesGql,
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<OfferingPageResolved> {
    const idOrSlug = route.paramMap.get('pageSlug')
    if (!idOrSlug) {
      return this.util.redirectToNotFound(state.url)
    }

    return this.contentService.getOfferingPage(idOrSlug).pipe(
      mergeMap(({page, offering}) => {
        const relatedOfferings$: Observable<IndustryOffering[]> = offering.industryPage?.proDirectoryIndustriesIds
          .length
          ? this.getIndustriesGql.fetch({industryIds: offering.industryPage.proDirectoryIndustriesIds}).pipe(
              map(res => {
                const industries = pickProperty(res.data, 'industriesByIds')
                return industries
                  .reduce<IndustryOffering[]>((prev, current) => {
                    prev.push(...current.offerings)
                    return prev
                  }, [])
                  .filter(o => offering.id !== o.id)
              }),
            )
          : of([] as IndustryOffering[])
        return relatedOfferings$.pipe(
          map(relatedOfferings => ({
            offering,
            page,
            relatedOfferings,
          })),
        )
      }),
      mergeMap(r =>
        this.getOfferingIntentionsGql.fetch({offeringId: r.offering.id}).pipe(
          map(intentionRes => {
            const props = {offering: r.offering.clientFacingTitle || r.offering.title}
            const intentions =
              intentionRes.data.offeringById?.intentions && intentionRes.data.offeringById?.areIntentionsEnabled
                ? intentionRes.data.offeringById.intentions
                : []
            return {
              content: r.page.content,
              cover: {
                ...r.page.cover,
                titleProps: props,
                subtitleProps: props,
              },
              slug: r.offering.slug,
              relatedOfferings: r.relatedOfferings,
              seo: r.page.seo,
              ...{
                ...r.offering,
                intentions,
              },
            }
          }),
        ),
      ),
      catchError(err => this.util.handleUnknownError(err)),
    )
  }
}
